/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo, numberOfProductsNoPlusSign } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { useIsLimitedFreeTrialEnabled } from 'bundles/enroll-common/utils/freeTrialOptimizationsUtils';
import CourseraLogo from 'bundles/page/components/CourseraLogo';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import {
  clearConvertFreeTrialData,
  getConvertFreeTrialData,
} from 'bundles/subscriptions-common/utils/blockCertInFreeTrialUtils';

import _t from 'i18n!nls/my-purchases-graphql';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles: Record<string, Interpolation<Theme>> = {
  logo: css`
    margin-bottom: 12px;
  `,
  courseraLogo: css`
    height: 16px;
  `,
  headingGroup: css`
    padding: 0 0 var(--cds-spacing-200) 0;
  `,
  listHead: css`
    font-size: 16px;
  `,
  list: css`
    padding-left: var(--cds-spacing-300);
  `,
  actions: css`
    padding-top: var(--cds-spacing-150);
  `,
};

const ConvertFreeTrialConfirmationModal = () => {
  const [isOpen, setIsOpen] = useState(true);
  const retrackedTrack = useRetracked();
  const { isLimitedFreeTrialEnabled } = useIsLimitedFreeTrialEnabled();

  const { Content, HeadingGroup, Actions } = Dialog;

  const { subscriptionId, userId, isCourseraPlusSubscription, productName } = getConvertFreeTrialData() ?? {};
  const hasNewLimitedFreeTrialCopy = isLimitedFreeTrialEnabled && (isCourseraPlusSubscription || Boolean(productName));

  if (!user.isAuthenticatedUser() || !userId || userId !== user.get().id) {
    return null;
  }

  const handleCloseModal = () => {
    clearConvertFreeTrialData();
    retrackedTrack({
      trackingData: { subscriptionId, userId },
      trackingName: 'convert_free_trial_confirmation_modal_close',
      action: 'click',
    });
    setIsOpen(false);
  };

  const getTitle = () => {
    if (isCourseraPlusSubscription) {
      return (
        <>
          <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={200} css={styles.logo} />
          <HeadingGroup css={styles.headingGroup}>{_t(`Welcome to Coursera Plus 🎉`)}</HeadingGroup>
        </>
      );
    }

    return (
      <div css={styles.headingGroup}>
        <CourseraLogo css={[styles.logo, styles.courseraLogo]} />
        <Typography2 component="h2" variant="titleSmall">
          {_t(`You're all set, #{learnerName} 🎉`, { learnerName: user.get().full_name })}
        </Typography2>
      </div>
    );
  };

  const getContent = () => {
    const dash = <span>&#8212;</span>;

    if (isCourseraPlusSubscription) {
      return (
        <>
          <Typography2 component="h3" variant="titleSmall" css={styles.listHead}>
            {_t('With your Plus subscription you can:')}
          </Typography2>
          <ul>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t('{findYourFit}{dash}try any course(s) with the {courseraPlus} badge')}
                  findYourFit={<strong>{_t('Find your fit')}</strong>}
                  dash={dash}
                  courseraPlus={
                    <Imgix src={logo.PLUS_PILL_BLUE} maxWidth={37} maxHeight={16} alt={_t('Coursera Plus')} />
                  }
                />
              </Typography2>
            </li>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t('{getHandsOnExperience}{dash}learn job-ready skills with over 500 projects')}
                  getHandsOnExperience={<strong>{_t('Get hands-on experience')}</strong>}
                  dash={dash}
                />
              </Typography2>
            </li>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t(
                    '{growYourCareer}{dash}skill up with access to over {numberOfProducts} learning programs'
                  )}
                  growYourCareer={<strong>{_t('Grow your career')}</strong>}
                  dash={dash}
                  numberOfProducts={numberOfProductsNoPlusSign}
                />
              </Typography2>
            </li>
          </ul>
        </>
      );
    }

    return (
      <Typography2 component="p">
        {_t(
          `Congratulations! You've taken a big step toward your goals. Your subscription is now active and your certificate is available.`
        )}
      </Typography2>
    );
  };

  const getLimitedFreeTrialContent = () => {
    if (isCourseraPlusSubscription) {
      return (
        <>
          <Typography2 component="p">{_t('With your subscription you can:')}</Typography2>
          <ul css={styles.list}>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t('{findYourFit} - Try any of the thousands of course(s) with the Plus badge')}
                  findYourFit={<strong>{_t('Find your fit')}</strong>}
                />
              </Typography2>
            </li>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t(
                    '{getHandsOnExperience} - Learn job ready skills with premium features, including Coursera AI and graded assignments'
                  )}
                  getHandsOnExperience={<strong>{_t('Get hands-on experience')}</strong>}
                />
              </Typography2>
            </li>
            <li>
              <Typography2 component="p">
                <FormattedMessage
                  message={_t(
                    '{turnYourLearningIntoProof} - Earn certificates that can be shared on your resume, LinkedIn, and CV.'
                  )}
                  turnYourLearningIntoProof={<strong>{_t('Turn your learning into proof')}</strong>}
                />
              </Typography2>
            </li>
          </ul>
        </>
      );
    }

    return (
      <Typography2 component="p">
        {_t(
          `Congratulations! You've taken a big step towards your goals. Your subscription for #{productName} is now active`,
          { productName }
        )}
      </Typography2>
    );
  };

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleCloseModal}
      data-testid="convert-free-trial-confirmation-modal"
    >
      {hasNewLimitedFreeTrialCopy && (
        <HeadingGroup>{_t(`You're all set #{learnerName}`, { learnerName: user.get().full_name })}</HeadingGroup>
      )}
      <Content id="dialog-content">
        {!hasNewLimitedFreeTrialCopy && getTitle()}
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="convert_free_trial_confirmation_modal"
          trackingData={{ subscriptionId, userId }}
        >
          {hasNewLimitedFreeTrialCopy ? getLimitedFreeTrialContent() : getContent()}
        </TrackedDiv>
      </Content>
      <Actions css={styles.actions}>
        <TrackedButton
          variant="primary"
          onClick={handleCloseModal}
          trackingName="convert_free_trial_confirmation_modal_continue_button"
        >
          {_t('Got it')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default ConvertFreeTrialConfirmationModal;
